import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { TypedDictionary, WindowService } from '@core/index';
import {
  anywhere,
  concreteSelection,
  FareSaleAirportSelection,
} from '@shared/fare-sale-sort-link/fare-sale-utils/types/fare-sale-list.type';
import {
  convertLegendToArray,
  FocusOn,
  Legend,
  RegionsCopy,
  SvgIconService,
} from '@shared/index';
import { IconCacheService } from '@shared/interceptors/icon-cache/icon-cache.service';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { FareSaleOriginSelection } from '@store/fare-sale/types';
import { getAllAirportsSortedAndGroupedByPm } from '@store/origins/api';
import { Airport, CountryWithAirports, Region } from '@store/origins/types';
import {
  containsWordAndPunctuation,
  JbAutocompleteOptionsGroup,
  JbDialogService,
  JbTabsComponent,
} from 'jb-component-library';
import { filter, prop, toUpper } from 'ramda';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { CityPairSelector } from '../city-pair-selector/types/city-pair-selector.type';
import {
  filterAirportsUtility,
  generateNoMatchText,
} from '../city-selector/utils/city-selector.utils';

@Component({
  selector: 'dot-region-selector-v2',
  templateUrl: './region-selector-v2.component.html',
  styleUrls: ['./region-selector-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RegionSelectorV2Component implements OnInit, AfterViewInit {
  private _destroy = inject(DestroyRef);

  @ViewChild('regionRef') regionRef: ElementRef;
  @ViewChild('airportName') airportName: ElementRef;
  @ViewChild('countryField') countryField: ElementRef;
  @ViewChild('tabsRef') tabsRef: JbTabsComponent;
  @Input() control: UntypedFormControl = new UntypedFormControl();
  @Input() citySelector: any;
  @Input() formWrapper: HTMLElement;
  @Input() filterOutPartner = false;
  @Input() hideLegend = false;
  @Input() customLegendMessage: string;
  @Input() isFaresale = false;
  @Input() label: string;
  @Input() groupedAirports: JbAutocompleteOptionsGroup[];
  @Input() separatePartnerCities: boolean;
  @Input('content')
  set regionsContent(content) {
    this.content = content;
    if (this.content.legends) {
      this.legends = convertLegendToArray(this.content.legends);
    }

    this.legends.forEach(legend => {
      if (legend.code.toLowerCase() === 'mint') {
        this.mintIconLegend = legend;
      } else if (legend.code.toLowerCase() === 'partner') {
        this.partnerIconLegend = legend;
      }
    });

    this.regionsCopy = this.content.citySelector.regionsCopy;

    // v2 select list
    this.selectListProps = {
      useV2: content.listVersion === 'v2',
      listIcons: booleanStringCheck(content.showIcons),
      cityInSubtext: booleanStringCheck(content.cityInSubtext),
      airportIconSrc: content.airportIconSrc,
      destinationIconSrc: content.destinationIconSrc,
      regionIconSrc: content.regionIconSrc,
    };
    this.iconCacheService.cacheAndPreloadIcons([
      content.airportIconSrc,
      content.destinationIconSrc,
      content.regionIconSrc,
    ]);
  }
  @Input('regions')
  set regions(allRegions: TypedDictionary<Region>) {
    if (Object.keys(this.regionsData).length === 0) {
      this.regionsData = allRegions;
      this.composeRegionsSelection();
    }
  }
  @Input('countries')
  set countries(countries: CountryWithAirports[]) {
    this.countriesData = countries;
    this.composeRegionsSelection();
  }

  @Output() fareSaleSelectedAirport: EventEmitter<FareSaleAirportSelection> =
    new EventEmitter();
  @Output() backButtonClick = new EventEmitter();
  @Output() inputFocused = new EventEmitter();

  transformedRegions: BehaviorSubject<any> = new BehaviorSubject({});
  currentRegionCodeInView = '';
  countryName = '';
  noMatchText = '';
  currentRegionReference;
  lastFocusedList;
  regionsCopy: RegionsCopy;
  regionsCollection: TypedDictionary<Region> = {};
  countriesByRegion: TypedDictionary<CountryWithAirports[]> = {};
  filteredCountriesByRegion: CountryWithAirports[] = [];
  regionsList = [];
  airportsList = [];
  allAirports: Airport[] = [];
  filteredAirports: Airport[] = [];
  content: CityPairSelector;
  legends: Legend[];
  mintIconLegend: Legend;
  partnerIconLegend: Legend;
  countriesData: CountryWithAirports[];
  regionsData: TypedDictionary<Region> = {};
  isMobile$: Observable<boolean> = this.windowService.isMobile;
  legendClassNames$: Observable<string>;

  // v2 select list properties
  selectListProps = {
    useV2: false,
    listIcons: false,
    cityInSubtext: false,
    airportIconSrc: '',
    destinationIconSrc: '',
    regionIconSrc: '',
  };

  constructor(
    private dialogService: JbDialogService,
    private windowService: WindowService,
    private svgIconService: SvgIconService,
    private iconCacheService: IconCacheService,
  ) {}

  ngOnInit(): void {
    this.legendClassNames$ = this.isMobile$.pipe(
      takeUntilDestroyed(this._destroy),
      map(isMobile => {
        if (isMobile) {
          return this.selectListProps.useV2 ? 'dn' : 'nl3 nr3';
        } else {
          return 'mh0';
        }
      }),
    );
    this.legends.forEach(legend =>
      this.svgIconService.addSvg(legend.image.src),
    );
    const allAirportsSortedAndGroupedByPm = getAllAirportsSortedAndGroupedByPm(
      this.countriesData,
      this.separatePartnerCities,
    );

    this.allAirports = allAirportsSortedAndGroupedByPm;
    this.filteredAirports = allAirportsSortedAndGroupedByPm;
  }

  ngAfterViewInit(): void {
    /* Focus on the element that countryfield's template ref variable is on.
    If it's mobile, tabsRef's element will be focused on instead.
    Optional chaining this.tabsRef lets us return undefined rather than raise an error
    when not mobile. */
    this.countryField.nativeElement.focus();
    this.tabsRef?.buttons.first.nativeElement.focus();
  }

  setNoMatchText(
    filteredAirports,
    airportCollection,
    airportCollectionHashMap,
  ): string {
    if (!!filteredAirports && filteredAirports.length) {
      return '';
    }
    // invalid route
    // before investing in expensive filter, see if city code was entered
    if (!!airportCollectionHashMap[toUpper(this.countryName)]) {
      return this.content.citySelector.noMatchText.invalidRoute;
    } else {
      const filteredAirportsCollection = filterAirportsUtility(
        airportCollection,
        airportCollectionHashMap,
        this.countryName,
      );
      if (!!filteredAirportsCollection && filteredAirportsCollection.length) {
        return this.content.citySelector.noMatchText.invalidRoute;
      }
    }
    // invalid airport
    return generateNoMatchText(
      this.countryName,
      this.content.citySelector.noMatchText.invalidAirport,
    );
  }

  filterCountries() {
    // Ignore whitespaces from start REF: DOT-10326
    const trimmedCountryName = this.countryName.trimStart();
    if (!trimmedCountryName) {
      this.filteredAirports = this.allAirports;
      this.filteredCountriesByRegion =
        this.countriesByRegion[this.currentRegionCodeInView];
    } else {
      this.filteredAirports = this.allAirports.filter(airport => {
        const matched =
          containsWordAndPunctuation(trimmedCountryName)(airport.name) ||
          containsWordAndPunctuation(trimmedCountryName)(airport.needle);
        return matched;
      });

      if (this.filteredAirports.length === 0) {
        const allAirportsHashMap = this.allAirports.reduce((acc, airport) => {
          acc[airport.code] = airport;
          return acc;
        }, {});

        this.noMatchText = this.setNoMatchText(
          this.filteredAirports,
          this.allAirports,
          allAirportsHashMap,
        );
      } else {
        this.noMatchText = '';
      }
    }
  }

  tabSelected(selectedIndex, regionsList) {
    this.currentRegionCodeInView = regionsList[selectedIndex].code;
    this.filterCountries();
  }

  filterRegions(event, regionCode): void {
    this.currentRegionCodeInView = regionCode;
    event.stopPropagation();
  }

  regionFocused(regionRef: ElementRef) {
    this.currentRegionReference = regionRef;
    this.lastFocusedList = FocusOn.REGION;
  }

  invisibleFocused() {
    if (this.lastFocusedList === FocusOn.REGION) {
      this.airportName.nativeElement.focus();
      return;
    }
    this.currentRegionReference.focus();
  }

  select(airport: Airport): void {
    if (!this.isFaresale) {
      this.control.setValue(airport);
    } else {
      const fareSaleAirport: FareSaleOriginSelection = {
        origin: airport.code,
        originName: airport.name,
      };
      this.fareSaleSelectedAirport.emit(concreteSelection(fareSaleAirport));
    }
    this.closeDialog();
  }

  cityFocused() {
    this.lastFocusedList = FocusOn.CITY;
  }

  selectAll(): void {
    this.fareSaleSelectedAirport.emit(anywhere);
    this.closeDialog();
  }

  handleRegionsKeydown(event, regionCode): void {
    if (event.keyCode === 13) {
      this.currentRegionCodeInView = regionCode;
      this.airportName.nativeElement.focus();
    }
  }

  groupCountriesByRegion(): void {
    const filteredSuggestions = filter(
      suggestion => !!suggestion.airports.length,
      this.countriesData,
    );
    if (filteredSuggestions.length > 0) {
      // this.currentRegionCodeInView = filteredSuggestions[0].code;
      this.currentRegionCodeInView = 'ALL';
    }
    this.countriesByRegion = this.groupByArrayProp(
      'region',
      filteredSuggestions,
    );
    let allRegions = [];
    Object.keys(this.countriesByRegion).forEach(key => {
      allRegions = allRegions.concat(this.countriesByRegion[key]);
    });
    this.countriesByRegion['ALL'] = allRegions;
    this.filterCountries();
  }

  groupByArrayProp(selector: string, arr: any[]) {
    const result = {};
    for (const elem of arr) {
      const property = prop(selector, elem);
      if (!!property) {
        for (const propVal of property) {
          !result[propVal]
            ? (result[propVal] = [elem])
            : result[propVal].push(elem);
        }
      }
    }
    return result;
  }

  /** Get a legend title based on substring search */
  getLegendTitle(str: 'mint' | 'partner') {
    return this.legends.find(l => l.code === str).title || '';
  }

  createDynamicRegions(): void {
    let dynamicRegions: TypedDictionary<Region> = {};
    dynamicRegions = { ...this.regionsData };

    for (const key in this.regionsData) {
      if (!this.countriesByRegion[this.regionsData[key].code]) {
        delete dynamicRegions[key];
      } else {
        dynamicRegions[key] = this.regionsData[key];
      }
    }

    this.transformedRegions.next({
      ALL: {
        code: 'ALL',
        name: 'All Airports',
      },
      ...dynamicRegions,
    });
  }

  closeDialog(): void {
    this.dialogService.closeDialog();
  }

  private composeRegionsSelection(): void {
    if (this.countriesData && this.regionsData) {
      this.groupCountriesByRegion();
      this.createDynamicRegions();
    }
  }
}
